import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Group, Select, SimpleGrid, Stack, Table, Text, ThemeIcon } from '@mantine/core';
import {
    IconCircleCheck,
    IconClipboardCheck,
    IconClock,
    IconUserOff,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { listOrganizations } from '../../api/resources';
import { DataPanel } from '../../components/DataPanel';
import { PageHeader } from '../../components/PageHeader';

/**
 * Cross-organization monitoring dashboard. The figures below are placeholder
 * data — swap each block for a `/reporting/*` aggregation once the backend
 * exposes per-organization monitoring totals. The organization filter is wired
 * to the real org list so it is ready to drive those queries.
 */

type Kpi = {
    key: string;
    label: string;
    value: string;
    icon: typeof IconClock;
    tone: 'default' | 'alert' | 'good';
};

const KPIS: Kpi[] = [
    { key: 'due', label: 'Due visits', value: '512', icon: IconClock, tone: 'alert' },
    { key: 'unassigned', label: 'Unassigned visits', value: '96', icon: IconUserOff, tone: 'default' },
    { key: 'pendingQa', label: 'Pending QA', value: '173', icon: IconClipboardCheck, tone: 'default' },
    { key: 'completed', label: 'Completed visits', value: '8,240', icon: IconCircleCheck, tone: 'good' },
];

type OrgRow = {
    name: string;
    activeCases: number;
    due: number;
    completed: number;
    pendingQa: number;
    materialChanges: number;
    completionRate: number;
};

const ORG_ROWS: OrgRow[] = [
    { name: 'Renmoney', activeCases: 3090, due: 184, completed: 2914, pendingQa: 62, materialChanges: 41, completionRate: 94 },
    { name: 'FairMoney', activeCases: 2410, due: 152, completed: 2180, pendingQa: 51, materialChanges: 28, completionRate: 91 },
    { name: 'Sun King', activeCases: 1340, due: 98, completed: 1896, pendingQa: 34, materialChanges: 17, completionRate: 95 },
];

const OUTCOMES = [
    { label: 'No material change', value: '8,143', color: 'green' },
    { label: 'Material change identified', value: '97', color: 'red' },
    { label: 'Follow-up required', value: '54', color: 'yellow' },
];

const QA_THROUGHPUT = [
    { label: 'Approved', value: '7,986', color: 'green' },
    { label: 'Rejected', value: '142', color: 'red' },
    { label: 'Avg. turnaround', value: '0.9 days', color: undefined },
];

const COVERAGE = [
    { label: 'Organizations', value: '4' },
    { label: 'Active field officers', value: '128' },
    { label: 'Regions covered', value: '17' },
];

type CaseTypeRow = {
    label: string;
    color: string;
    activeCases: number;
    scheduled: number;
    completed: number;
    materialChanges: number;
};

const CASE_TYPE_ROWS: CaseTypeRow[] = [
    { label: 'Lending', color: 'blue', activeCases: 4900, scheduled: 8200, completed: 5300, materialChanges: 62 },
    { label: 'PAYG', color: 'grape', activeCases: 1880, scheduled: 2640, completed: 1910, materialChanges: 21 },
    { label: 'Mobility', color: 'teal', activeCases: 820, scheduled: 1110, completed: 1030, materialChanges: 14 },
];

const num = (n: number) => n.toLocaleString();

function KpiCard({ kpi }: { kpi: Kpi }) {
    const iconColor = kpi.tone === 'alert' ? 'red' : kpi.tone === 'good' ? 'green' : 'yellow';
    const Icon = kpi.icon;
    return (
        <DataPanel>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
                <div>
                    <Text size="sm" c="dimmed" fw={600}>
                        {kpi.label}
                    </Text>
                    <Text fz={28} fw={800} mt={6} lh={1}>
                        {kpi.value}
                    </Text>
                </div>
                <ThemeIcon variant="light" color={iconColor} size="lg" radius="md">
                    <Icon size={18} />
                </ThemeIcon>
            </Group>
        </DataPanel>
    );
}

function StatList({ title, rows }: { title: string; rows: { label: string; value: string; color?: string }[] }) {
    return (
        <DataPanel>
            <Text fw={700} size="md" mb="md">
                {title}
            </Text>
            <Stack gap="sm">
                {rows.map((r) => (
                    <Group key={r.label} justify="space-between" wrap="nowrap">
                        <Text size="sm" c="dimmed">
                            {r.label}
                        </Text>
                        <Text size="sm" fw={800} c={r.color}>
                            {r.value}
                        </Text>
                    </Group>
                ))}
            </Stack>
        </DataPanel>
    );
}

export function DashboardPage() {
    const { t } = useTranslation();
    const [orgFilter, setOrgFilter] = useState<string | null>(null);

    const orgsQ = useQuery({
        queryKey: ['organizations-all'],
        queryFn: () => listOrganizations({ page: 1, limit: 500 }),
    });
    const orgOptions = (orgsQ.data?.data ?? []).map((o) => ({ value: o.id, label: o.name }));

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.dashboard.title')}
                description={t('features.dashboard.description')}
                actions={
                    <Select
                        placeholder={t('features.dashboard.allOrganizations')}
                        clearable
                        searchable
                        w={240}
                        data={orgOptions}
                        value={orgFilter}
                        onChange={setOrgFilter}
                    />
                }
            />

            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
                {KPIS.map((kpi) => (
                    <KpiCard key={kpi.key} kpi={kpi} />
                ))}
            </SimpleGrid>

            <DataPanel>
                <Text fw={700} size="md" mb="md">
                    {t('features.dashboard.byOrganization')}
                </Text>
                <Table.ScrollContainer minWidth={720}>
                    <Table verticalSpacing="sm">
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('fields.organization')}</Table.Th>
                                <Table.Th>{t('features.dashboard.activeCases')}</Table.Th>
                                <Table.Th>{t('fields.dueAt')}</Table.Th>
                                <Table.Th>{t('features.dashboard.completed')}</Table.Th>
                                <Table.Th>{t('features.dashboard.pendingQa')}</Table.Th>
                                <Table.Th>{t('features.dashboard.materialChanges')}</Table.Th>
                                <Table.Th>{t('features.dashboard.completionRate')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {ORG_ROWS.map((r) => (
                                <Table.Tr key={r.name}>
                                    <Table.Td fw={600}>{r.name}</Table.Td>
                                    <Table.Td>{num(r.activeCases)}</Table.Td>
                                    <Table.Td>{num(r.due)}</Table.Td>
                                    <Table.Td>{num(r.completed)}</Table.Td>
                                    <Table.Td>{num(r.pendingQa)}</Table.Td>
                                    <Table.Td>{num(r.materialChanges)}</Table.Td>
                                    <Table.Td>
                                        <Badge color="green" variant="light">
                                            {r.completionRate}%
                                        </Badge>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </DataPanel>

            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
                <StatList title={t('features.dashboard.monitoringOutcomes')} rows={OUTCOMES} />
                <StatList title={t('features.dashboard.qaThroughput')} rows={QA_THROUGHPUT} />
                <StatList title={t('features.dashboard.coverage')} rows={COVERAGE} />
            </SimpleGrid>

            <DataPanel>
                <Text fw={700} size="md" mb="md">
                    {t('features.dashboard.byCaseType')}
                </Text>
                <Table.ScrollContainer minWidth={640}>
                    <Table verticalSpacing="sm">
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('fields.caseType')}</Table.Th>
                                <Table.Th>{t('features.dashboard.activeCases')}</Table.Th>
                                <Table.Th>{t('features.dashboard.scheduled')}</Table.Th>
                                <Table.Th>{t('features.dashboard.completed')}</Table.Th>
                                <Table.Th>{t('features.dashboard.materialChanges')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {CASE_TYPE_ROWS.map((r) => (
                                <Table.Tr key={r.label}>
                                    <Table.Td>
                                        <Badge color={r.color} variant="light">
                                            {r.label}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>{num(r.activeCases)}</Table.Td>
                                    <Table.Td>{num(r.scheduled)}</Table.Td>
                                    <Table.Td>{num(r.completed)}</Table.Td>
                                    <Table.Td>{num(r.materialChanges)}</Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </DataPanel>
        </Stack>
    );
}
